import AuthModalButton from 'components/AuthModalButton/AuthModalButton';
import { useApi } from 'contexts/Api/Api';
import { useContent } from 'contexts/Content/Content';
import { getUrl } from 'helpers';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Container,
    Content,
    Logo,
    LogoMobile,
    LogoPicture,
    LogoWrap,
    PortionMarking,
    Tagline,
    Text,
    Title,
    UserActionLink,
    UserActions,
    UserContainer,
    UserImage,
    UserName,
    UserPicture,
    UserProfile,
    UserProfileMenu,
    UserProfileMenuButton,
    UserProfileMenuLink,
    Wrap,
} from './Header.styled';

export type HeaderAuthState = 'landing_guest' | 'landing_member' | 'game_member';

export function getHeaderAuthState(
    path: string,
    authEnabled: boolean,
    authenticated: boolean
): HeaderAuthState | null {
    if (!authEnabled) return null;
    if (path === '/') return authenticated ? 'landing_member' : 'landing_guest';
    if ((path === '/game' || path === '/simulation') && authenticated) return 'game_member';
    return null;
}

export default function Header({ children, style, visible = true }: HeaderProps) {
    const [openProfileMenu, setOpenProfileMenu] = useState(false);
    const api = useApi();
    const content = useContent();
    const router = useRouter();
    const userContainerRef = useRef<HTMLDivElement>(null);

    const { background, backgroundMobile, foreground, logo, logoLink, logoMobile, tagline, textOffset, title } =
        content.header;
    const { portionMarking } = content.general;

    const headerAuthState = useMemo(
        () => getHeaderAuthState(router.pathname, api.authenticate, api.authenticated),
        [router.pathname, api.authenticate, api.authenticated]
    );

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (openProfileMenu && userContainerRef.current && !userContainerRef.current.contains(e.target as Node)) {
                setOpenProfileMenu(false);
            }
        },
        [openProfileMenu]
    );

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            setOpenProfileMenu(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('click', handleClick);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('click', handleClick);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClick, handleKeyDown]);

    function handleLogout() {
        void api.logout();
        setOpenProfileMenu(false);
    }

    return (
        <Wrap style={style}>
            {portionMarking && <PortionMarking>{portionMarking}</PortionMarking>}
            {visible && (
                <Container $background={background} $backgroundMobile={backgroundMobile || background}>
                    <LogoWrap>
                        <a data-testid="header-logo-link" href={logoLink || '/'}>
                            <LogoPicture>
                                <LogoMobile alt="Logo" src={getUrl(logoMobile || logo)} />
                                <Logo alt="Logo" src={getUrl(logo)} />
                            </LogoPicture>
                        </a>
                    </LogoWrap>
                    <Content $foreground={foreground}>
                        <Text href={getUrl('/')} $foreground={foreground} $offset={textOffset}>
                            <Title>{title}</Title>
                            {tagline && <Tagline>{tagline}</Tagline>}
                        </Text>
                        {children}
                        {headerAuthState !== null && (
                            <>
                                {headerAuthState === 'landing_guest' ? (
                                    <UserActions>
                                        <AuthModalButton data-testid="header-login" variant="white" onClick={api.showAuthModal}>
                                            Log in
                                        </AuthModalButton>
                                        <AuthModalButton data-testid="header-join-now" onClick={() => api.showAuthModal('registration')}>
                                            Join Now
                                        </AuthModalButton>
                                    </UserActions>
                                ) : (
                                    <UserActions>
                                        {headerAuthState === 'landing_member' && (
                                            <UserActionLink data-testid="header-my-kit" href="/mykit">My Kit</UserActionLink>
                                        )}
                                        <UserContainer ref={userContainerRef}>
                                            <UserProfile
                                                aria-label="Open account menu"
                                                data-testid="header-user-menu"
                                                onClick={() => setOpenProfileMenu(!openProfileMenu)}
                                            >
                                                <UserName>{api.userInfo?.first_name || 'Account'}</UserName>
                                                <UserPicture>
                                                    <UserImage
                                                        alt="avatar"
                                                        src={api.userInfo?.avatar || getUrl('/assets/user-icon.svg')}
                                                    />
                                                </UserPicture>
                                            </UserProfile>
                                            {openProfileMenu && (
                                                <UserProfileMenu>
                                                    <UserProfileMenuLink data-testid="header-my-profile" href="/user">My Profile</UserProfileMenuLink>
                                                    <UserProfileMenuButton data-testid="header-logout" onClick={handleLogout}>Log Out</UserProfileMenuButton>
                                                </UserProfileMenu>
                                            )}
                                        </UserContainer>
                                    </UserActions>
                                )}
                            </>
                        )}
                    </Content>
                </Container>
            )}
        </Wrap>
    );
}
